import { Helmet } from "react-helmet";
import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBoxOpen,
  FaTriangleExclamation,
  FaXmark,
  FaCircleCheck,
  FaBoxArchive,
  FaClockRotateLeft,
  FaBoxesPacking,
  FaWrench,
  FaRightLeft,
  FaCirclePlus,
  FaCircleMinus,
} from "react-icons/fa6";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { Header } from "../../../../components/Header";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { BtnBack } from "../../../../components/buttons/BtnBack";
import { useHookLogisticMonitoringDetail } from "../../../../hooks/useHookLogisticMonitoringDetail";
import { GlassPanel } from "./component/glass_panel";
import { FaExclamationTriangle } from "react-icons/fa";
import { StokBadgeLogistic } from "./component/stock_badge";
import { InfoRowLogistic } from "./component/info_row";
import { ProgressBarLogistic } from "./component/progress_bar";


export const LogisticMonitoringDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);

  const { data, loading, error, refreshData } = useHookLogisticMonitoringDetail({ id });

  const distribusi = useMemo(() => {
    if (!data) return { terpakai: 0, tidakDigunakan: 0, rusak: 0 };
    const total = data?.total_stok;
    return {
      terpakai: total > 0 ? ((data?.used_stock / total) * 100).toFixed(1) : 0,
      tidakDigunakan: total > 0 ? ((data?.unused_stock / total) * 100).toFixed(1) : 0,
      rusak: total > 0 ? ((data?.damaged_stock / total) * 100).toFixed(1) : 0,
    };
  }, [data]);

  // Loading state
  if (loading) {
    return (
      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="h-6 w-48 bg-slate-700/60 rounded-md animate-pulse" />
        </Header>
        <main className="p-4 lg:p-6 space-y-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-40 rounded-2xl bg-slate-800/30 animate-pulse" style={{ border: "1px solid rgba(51, 65, 85, 0.2)" }} />
          ))}
        </main>
      </DashboardLayout>
    );
  }

  // Not found
  if (!data) {
    return (
      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <BtnBack />
          <h2 className="text-base md:text-xl font-bold text-gray-100">Detail Barang</h2>
          <div />
        </Header>
        <main className="p-4 lg:p-6">
          <GlassPanel className="py-20 text-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}>
              <FaBoxOpen className="text-3xl text-slate-600" />
            </div>
            <p className="text-slate-300 font-semibold text-lg">Barang tidak ditemukan</p>
            <p className="text-sm text-slate-500 mt-2 mb-6">ID barang yang dicari tidak tersedia dalam database.</p>
            <BtnDefault onClick={() => navigate("/dashboard/logistic/barang")}>
              Kembali ke Monitoring
            </BtnDefault>
          </GlassPanel>
        </main>
      </DashboardLayout>
    );
  }

  

  return (
    <>
      <Helmet>
        {/* <title>{data?.name || "Detail Barang"} - Radius Trend</title>
        <meta name="description" content={`Detail stok dan riwayat ${data?.name} (${data?.type})`} /> */}
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard/logistic/barang")}
              className="p-2 rounded-lg text-slate-400 hover:text-gray-100 hover:bg-slate-700/40 transition-all"
              aria-label="Kembali"
            >
              <FaArrowLeft className="text-sm" />
            </button>
            <div>
              <h2 className="text-base md:text-xl font-bold text-gray-100 leading-tight">
                {data?.name}
              </h2>
              <p className="text-xs text-slate-500 hidden sm:block">{data?.category?.name} • {data?.type}</p>
            </div>
          </div>
          <StokBadgeLogistic stok={data?.total_stok} minStock={data?.min_stock} size="lg" />
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">

          {/* ===== Alert Banner (hanya jika kritis/habis) ===== */}
          {(data?.stock < data?.min_stock) && (
            <GlassPanel alert className="!p-4 flex items-start gap-3">
              <FaExclamationTriangle className="text-red-400 mt-0.5 shrink-0" size={18} />
              <div>
                <p className="text-sm font-bold text-red-300">
                  {data?.stock == 0 ? "Stok Habis!" : `Stok Kritis — Sisa ${data?.stok} ${data?.unit}`}
                </p>
                <p className="text-sm text-red-400/80 mt-0.5">
                  {data?.stock == 0
                    ? "Barang ini sudah tidak tersedia. Segera lakukan pengadaan untuk menghindari gangguan layanan."
                    : `Stok berada di bawah batas minimum (${data?.min_stock} ${data?.unit}). Segera ajukan pengadaan untuk menjaga ketersediaan.`}
                </p>
              </div>
            </GlassPanel>
          )}

          {/* ===== Info Cards Grid ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* ---- Kolom 1: Info Barang ---- */}
            <GlassPanel className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-5">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm"
                  style={{
                    background: data?.total_stock < data?.min_stock
                      ? "linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.05))"
                      : "linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 119, 182, 0.08))",
                    border: data?.total_stock < data?.min_stock ? "1px solid rgba(239, 68, 68, 0.15)" : "1px solid rgba(0, 180, 216, 0.15)",
                    color: data?.total_stock < data?.min_stock ? "#f87171" : "#48CAE4",
                  }}
                >
                  {data?.name ? data?.name.charAt(0).toUpperCase() : "—"}
                </div>
                <div>
                  <h3 className={`text-sm font-bold ${data?.total_stock < data?.min_stock ? "text-red-300" : "text-gray-100"}`}>
                    {data?.name}
                  </h3>
                  <span className="inline-flex px-2 py-0.5 mt-0.5 bg-slate-700/50 text-slate-400 text-[10px] font-semibold rounded-md border border-slate-600/30">
                    {data?.category?.name}
                  </span>
                </div>
              </div>

              <div className="space-y-0">
                {/* <InfoRowLogistic label="ID Barang" value={`BRG-${String(data?.id).padStart(4, "0")}`} mono /> */}
                <InfoRowLogistic label="Jenis" value={data?.type} />
                <InfoRowLogistic label="Kategori" value={data?.category?.name} />
                <InfoRowLogistic label="Status Stok" value={
                  data?.total_stock == 0 ? "Habis" : data?.total_stock < data?.min_stock ? "Kritis" : "Aman"
                } danger={data?.total_stock < data?.min_stock} />
                <InfoRowLogistic label="Batas Minimum" value={`${data?.min_stock} unit`} mono />
              </div>
            </GlassPanel>

            {/* ---- Kolom 2: Distribusi Stok ---- */}
            <GlassPanel className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-5">
                <FaBoxesPacking className="text-cyan-500" size={16} />
                <h3 className="text-sm font-bold text-gray-100 uppercase tracking-wider">Distribusi Stok</h3>
              </div>

              {/* Summary numbers */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 rounded-xl" style={{ background: "rgba(0, 180, 216, 0.06)", border: "1px solid rgba(0, 180, 216, 0.12)" }}>
                  <p className="text-2xl font-bold text-cyan-400">{data?.total_stock}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mt-1">Total Stok</p>
                </div>
                <div className="text-center p-3 rounded-xl" style={{ background: "rgba(59, 130, 246, 0.06)", border: "1px solid rgba(59, 130, 246, 0.12)" }}>
                  <p className="text-2xl font-bold text-blue-400">{data?.used_stock}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mt-1">Terpakai</p>
                </div>
                <div className="text-center p-3 rounded-xl" style={{ background: "rgba(245, 158, 11, 0.06)", border: "1px solid rgba(245, 158, 11, 0.12)" }}>
                  <p className="text-2xl font-bold text-amber-400">{data?.unused_stock}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mt-1">Tdk Digunakan</p>
                </div>
                <div className="text-center p-3 rounded-xl" style={{ background: data?.damaged_stock > 0 ? "rgba(249, 115, 22, 0.06)" : "rgba(51, 65, 85, 0.15)", border: data?.damaged_stock > 0 ? "1px solid rgba(249, 115, 22, 0.12)" : "1px solid rgba(51, 65, 85, 0.15)" }}>
                  <p className={`text-2xl font-bold ${data?.damaged_stock > 0 ? "text-orange-400" : "text-slate-500"}`}>{data?.damaged_stock}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider mt-1">Rusak</p>
                </div>
              </div>

              {/* Progress bars */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-blue-400">Terpakai</span>
                    <span className="text-xs font-mono text-slate-400">{distribusi.terpakai}% ({data?.used_stock} ${data?.unit})</span>
                  </div>
                  <ProgressBarLogistic value={data?.used_stock} max={data?.total_stok} color="blue" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-amber-400">Tidak Digunakan</span>
                    <span className="text-xs font-mono text-slate-400">{distribusi.tidakDigunakan}% ({data?.unused_stock} ${data?.unit})</span>
                  </div>
                  <ProgressBarLogistic value={data?.totalTidakDigunakan} max={data?.stok} color="amber" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-orange-400">Rusak</span>
                    <span className="text-xs font-mono text-slate-400">{distribusi.rusak}% ({data?.damaged_stock} ${data?.unit})</span>
                  </div>
                  <ProgressBarLogistic value={data?.damaged_stock} max={data?.total_stok} color="orange" />
                </div>
              </div>

              {/* Stok health indicator */}
              <div className="mt-6 pt-4" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Kesehatan Stok</span>
                  <span className={`text-xs font-bold ${data?.total_stock == 0 ? "text-red-400" : data?.total_stock < data?.min_stock ? "text-red-400" : "text-emerald-400"}`}>
                    {data?.total_stock == 0 ? "0%" : data?.total_stock < data?.min_stock ? `${Math.round((data?.total_stok / data?.min_stock) * 100)}% dari minimum` : `${Math.round((data?.total_stok / (data?.min_stock * 3)) * 100)}% (optimal)`}
                  </span>
                </div>
                <ProgressBarLogistic
                  value={data?.total_stock}
                  max={data?.min_stock * 3}
                  color={data?.total_stock == 0 ? "red" : data?.total_stock < data?.min_stock ? "red" : "emerald"}
                />
                <div className="flex justify-between mt-1.5">
                  <span className="text-[10px] text-red-500/60 font-mono">0</span>
                  <span className="text-[10px] text-red-400/80 font-mono">min {data?.min_stock}</span>
                  <span className="text-[10px] text-emerald-500/60 font-mono">optimal {data?.min_stock * 3}</span>
                </div>
              </div>
            </GlassPanel>
          </div>

        </main>
      </DashboardLayout>
    </>
  );
};